import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { EntityList, PageHead } from '../../components/EntityList';
import AccountService from '../../services/account.service';
import { getError } from '../../utils';
import AssetEditor from './AssetEditor';
import { useEditorReturn } from '../../hooks/useEditorReturn';
import { normalizeReturnTo } from '../../utils/editorReturn';

function AddAsset() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const isFromAccount = pathname.includes('/accounts/account/');
	const [preloadOwner, setPreloadOwner] = useState(null);
	// When opened from another editor's "crear nueva propiedad" typeahead link
	// (e.g. ContractEditor.jsx), returnTo carries the caller's path so we can
	// hand the newly created asset back to it instead of always landing on
	// the assets list / account page.
	const returnTo = searchParams.get('returnTo');
	const { onReturn, returnLabel } = useEditorReturn();

	useEffect(() => {
		if (!isFromAccount || !id) {
			return;
		}
		let isCurrent = true;
		AccountService.getDetails(id)
			.then(({ data }) => {
				if (isCurrent) {
					setPreloadOwner(data);
				}
			})
			.catch((ex) => toast.error(getError(ex)));
		return () => {
			isCurrent = false;
		};
	}, [isFromAccount, id]);

	const backTo = isFromAccount ? `/AdminScreen/accounts/account/${id}` : '/AdminScreen/assets';

	return (
		<EntityList>
			<Helmet>
				<title>Nuevo Activo</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: 'Activos fijos', to: '/AdminScreen/assets' },
					{ label: 'Nuevo Activo' },
				]}
				title='Nuevo Activo'
				subtitle='Completá los datos principales del activo.'
			/>
			<AssetEditor
				mode='create'
				disableOwner={isFromAccount}
				preloadOwner={preloadOwner}
				onCancel={() => navigate(normalizeReturnTo(returnTo) || backTo)}
				onReturn={onReturn}
				returnLabel={returnLabel}
				allowCreateRoundTrip
				onSaved={(savedAssetId) => {
					if (returnTo) {
						// Resolved against our own origin so an absolute/protocol-relative
						// returnTo can't navigate off-site — only pathname+search are used.
						const target = new URL(returnTo, window.location.origin);
						target.searchParams.set('propertyId', savedAssetId);
						navigate(`${target.pathname}${target.search}`);
					} else {
						navigate(`/AdminScreen/assets/asset/${savedAssetId}`);
					}
				}}
				saveLabel='Agregar'
			/>
		</EntityList>
	);
}

export default AddAsset;
